import { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@store/index';
import { apiClient, setAccessToken, SESSION_EXPIRED_KEY } from '@services/api';
import OAuthButtons from '@components/OAuthButtons';
import { apiErrorCode, toDisplayError } from '../utils/apiError';
import { readPendingInvitationToken } from '../utils/pendingInvitationToken';

// Validation schema
const loginSchema = z.object({
  email: z.string().min(1, 'auth.validation.emailRequired').email('auth.validation.emailInvalid'),
  password: z.string().min(1, 'auth.validation.passwordRequired'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginResponse {
  success: boolean;
  data: {
    user: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      role: string;
    };
    accessToken: string;
    refreshJti: string;
    expiresIn: number;
  };
}

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuthStore();

  // REV-678: RegisterPage has navigated here with `state.message` since
  // REV-521, and this page read `state.from` while dropping `state.message` on
  // the floor. Registration therefore completed in total silence: the user was
  // bounced to /login with no indication that anything had happened, let alone
  // that a verification email was waiting. Same shape as REV-706 — the producer
  // sends a message, the consumer discards it.
  const handoffMessage = (location.state as { message?: string } | null)?.message ?? null;

  // REV-802: set by the API client when a session ended on its own (the
  // refresh cookie could no longer renew it), so the user is told why they are
  // here instead of just landing on a login form. Read in the initializer and
  // cleared in an effect: StrictMode runs initializers twice, and clearing it
  // in the first would leave the second with nothing to show.
  const [sessionExpired] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    } catch {
      // sessionStorage can throw in private-mode/sandboxed contexts.
    }
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isAccountNotActive, setIsAccountNotActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Where to go after login: router state (set by ProtectedRoute or a link
  // that preserved its origin) wins; otherwise fall back to a path stashed by
  // the 401 interceptor before its hard redirect (REV-521). Then, if an
  // invitation token is parked for a sign-in round trip, back to the
  // invitation: a recipient can reach sign-in by a link that carries no return
  // path, such as the AuthLayout logo (REV-878). Default: home.
  const stashedRedirect = (() => {
    try {
      return sessionStorage.getItem('postLoginRedirect');
    } catch {
      return null;
    }
  })();
  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    stashedRedirect ||
    (readPendingInvitationToken() ? '/invites/accept' : '/');

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setServerError(null);
    setIsSubmitting(true);

    try {
      const response = await apiClient.post<LoginResponse>('/v1/auth/login', data);

      // REV-802: held in memory by the API client, never in localStorage.
      setAccessToken(response.data.accessToken);

      // Update auth store
      setUser(response.data.user);

      // Redirect to original destination or home
      try {
        sessionStorage.removeItem('postLoginRedirect');
      } catch {
        // ignore — see the stash comment above
      }
      void navigate(from, { replace: true });
    } catch (error: unknown) {
      // REV-549: read the code through apiErrorCode rather than the raw
      // top-level field. The API emits it in both positions during the
      // migration; reading the sibling directly works only until it is dropped,
      // and would then stop matching with no error and no failing test.
      if (apiErrorCode(error) === 'ACCOUNT_NOT_ACTIVE') {
        setIsAccountNotActive(true);
        setServerError(t('auth.errors.accountNotActive'));
      } else {
        setIsAccountNotActive(false);
        setServerError(toDisplayError(error, t('auth.errors.loginFailed')));
      }
      setResendSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleResendVerification = async () => {
    const email = getValues('email');
    if (!email) return;

    setIsResending(true);
    setResendSuccess(false);

    try {
      await apiClient.post('/v1/auth/resend-verification', { email });
      setResendSuccess(true);
      setServerError(null);
      setIsAccountNotActive(false);
    } catch {
      setServerError(t('auth.errors.resendVerificationFailed'));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
          {t('auth.login.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('auth.login.subtitle')}
        </Typography>
      </Box>

      {/* Why the user is here, when their session ended on its own (REV-802) */}
      {sessionExpired && (
        <Alert severity="info" sx={{ mb: 3 }} role="status">
          {t('auth.login.sessionExpired')}
        </Alert>
      )}

      {/* Message handed over by another route (registration, REV-678) */}
      {handoffMessage && !resendSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} role="status">
          {handoffMessage}
        </Alert>
      )}

      {/* Resend verification success */}
      {resendSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} role="status">
          {t('auth.errors.resendVerificationSuccess')}
        </Alert>
      )}

      {/* Server error alert */}
      {serverError && (
        <Alert severity={isAccountNotActive ? 'warning' : 'error'} sx={{ mb: 3 }} role="alert">
          <Box>
            {serverError}
            {isAccountNotActive && (
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResendVerification}
                  disabled={isResending}
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 600 }}
                >
                  {isResending ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                  {t('auth.errors.resendVerification')}
                </Button>
              </Box>
            )}
          </Box>
        </Alert>
      )}

      {/* Login form */}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('email')}
          label={t('auth.fields.email')}
          type="email"
          autoComplete="email"
          error={!!errors.email}
          helperText={errors.email ? t(errors.email.message as string) : undefined}
          disabled={isSubmitting}
          sx={{ mb: 2 }}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: {
              'aria-describedby': errors.email ? 'email-error' : undefined,
            },
          }}
        />

        <TextField
          {...register('password')}
          label={t('auth.fields.password')}
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          error={!!errors.password}
          helperText={errors.password ? t(errors.password.message as string) : undefined}
          disabled={isSubmitting}
          sx={{ mb: 1 }}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    onClick={handleTogglePassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
            htmlInput: {
              'aria-describedby': errors.password ? 'password-error' : undefined,
            },
          }}
        />

        {/* Forgot password link */}
        <Box sx={{ textAlign: 'right', mb: 3 }}>
          <Link component={RouterLink} to="/forgot-password" variant="body2">
            {t('auth.login.forgotPassword')}
          </Link>
        </Box>

        {/* Submit button */}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isSubmitting}
          sx={{ mb: 2 }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('auth.login.submit')}
        </Button>
      </Box>

      {/* OAuth buttons */}
      {/* REV-677: no Divider here. OAuthButtons returns null when no providers
          are configured, which is the default — so a divider owned by this page
          left TWO adjacent rules with nothing between them, reading as a gap
          where something failed to load. The divider that belongs to the OAuth
          block now lives inside it, and appears only when it renders. */}
      <OAuthButtons mode="login" />

      {/* This one is unconditional: it separates the form from the link below
          it, which is there whether or not OAuth is configured. */}
      <Divider sx={{ my: 3 }} />

      {/* Register link */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('auth.login.noAccount')}{' '}
          <Link component={RouterLink} to="/register">
            {t('auth.login.registerLink')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}

export default LoginPage;
